<template>
  <!-- CP-96: with 0 reviews nothing renders here (no 0-star summary, no
       filter/sort bar, no invented "be the first" CTA). The parent hides
       the Reviews card from the emitted count. -->
  <div v-if="hasAnyReviews" class="review-list">
    <div class="review-summary">
      <div class="summary-rating">
        <div class="average-rating">
          <span class="rating-value">{{ summary.average_rating }}</span>
          <div class="stars-large">
            <i 
              v-for="n in 5" 
              :key="n"
              :class="['fas', 'fa-star', { 'filled': n <= Math.round(summary.average_rating), 'empty': n > Math.round(summary.average_rating) }]"
            ></i>
          </div>
        </div>
        <div class="rating-count">
          Based on {{ summary.total_reviews }} {{ summary.total_reviews === 1 ? 'review' : 'reviews' }}
        </div>
      </div>
    </div>
    
    <div class="review-filters">
      <select v-model="selectedRating" @change="loadReviews" class="filter-select">
        <option value="">All Ratings</option>
        <option value="5">5 Stars</option>
        <option value="4">4 Stars</option>
        <option value="3">3 Stars</option>
        <option value="2">2 Stars</option>
        <option value="1">1 Star</option>
      </select>
      
      <select v-model="sortBy" @change="loadReviews" class="filter-select">
        <option value="date">Newest First</option>
        <option value="rating">Highest Rating</option>
        <option value="helpful">Most Helpful</option>
      </select>
    </div>
    
    <div v-if="loading" class="loading-state">
      <p>Loading reviews...</p>
    </div>
    
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
    </div>
    
    <div v-else-if="reviews.length === 0" class="no-reviews">
      <p>No reviews match this rating.</p>
    </div>
    
    <div v-else class="reviews-container">
      <review-card 
        v-for="review in reviews" 
        :key="review.id"
        :review="review"
        @helpful="handleHelpful"
      />
    </div>
    
    <div v-if="pagination && pagination.pages > 1" class="pagination">
      <button 
        @click="changePage(pagination.page - 1)"
        :disabled="!pagination.has_prev"
        class="page-btn"
      >
        Previous
      </button>
      <span class="page-info">
        Page {{ pagination.page }} of {{ pagination.pages }}
      </span>
      <button 
        @click="changePage(pagination.page + 1)"
        :disabled="!pagination.has_next"
        class="page-btn"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import ReviewCard from './ReviewCard.vue';
import { hasReviews } from '@/utils/detailModules';

export default {
  name: 'ReviewList',
  components: {
    ReviewCard
  },
  props: {
    cruiseId: {
      type: Number,
      required: true
    }
  },
  emits: ['count'],
  setup(props, { emit }) {
    const reviews = ref([]);
    const summary = ref(null);
    const pagination = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const selectedRating = ref('');
    const sortBy = ref('date');
    const currentPage = ref(1);
    
    const loadReviews = async (page = 1) => {
      loading.value = true;
      error.value = null;
      currentPage.value = page;
      
      try {
        const params = {
          page: page,
          per_page: 10
        };
        
        if (selectedRating.value) {
          params.rating = parseInt(selectedRating.value);
        }
        
        if (sortBy.value) {
          params.sort_by = sortBy.value;
        }
        
        const response = await axios.get(`/api/cruises/${props.cruiseId}/reviews`, { params });
        reviews.value = response.data.reviews;
        pagination.value = response.data.pagination;
        summary.value = response.data.summary;
      } catch (err) {
        error.value = 'Failed to load reviews';
        console.error('Error loading reviews:', err);
      } finally {
        emit('count', hasReviews(summary.value) ? Number(summary.value.total_reviews) : 0);
        loading.value = false;
      }
    };
    
    const changePage = (page) => {
      if (page >= 1 && page <= pagination.value.pages) {
        loadReviews(page);
      }
    };
    
    const handleHelpful = (reviewId) => {
      // In a real implementation, this would call an API to increment helpful count
      const review = reviews.value.find(r => r.id === reviewId);
      if (review) {
        review.helpful_count = (review.helpful_count || 0) + 1;
      }
    };
    
    // Total approved reviews for this cruise (unfiltered). Controls show only
    // when there is at least one, so a rating filter with no matches keeps
    // the bar to switch back.
    const hasAnyReviews = computed(() => hasReviews(summary.value));
    
    onMounted(() => {
      loadReviews();
    });
    
    watch(() => props.cruiseId, () => {
      summary.value = null;
      reviews.value = [];
      selectedRating.value = '';
      loadReviews();
    });
    
    return {
      hasAnyReviews,
      reviews,
      summary,
      pagination,
      loading,
      error,
      selectedRating,
      sortBy,
      loadReviews,
      changePage,
      handleHelpful
    };
  }
};
</script>

<style scoped>
.review-list {
  width: 100%;
}

.review-summary {
  background-color: var(--cs-d-surface-alt);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  text-align: center;
}

.summary-rating {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.average-rating {
  display: flex;
  align-items: center;
  gap: 16px;
}

.rating-value {
  font-size: 48px;
  font-weight: bold;
  color: var(--cs-d-link);
}

.stars-large {
  display: flex;
  gap: 4px;
}

.stars-large i {
  font-size: 24px;
}

.stars-large i.filled {
  color: #fbbc05;
}

.stars-large i.empty {
  color: #dadce0;
}

.rating-count {
  font-size: 14px;
  color: var(--cs-d-muted);
}

.review-filters {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid var(--cs-d-border);
  border-radius: 6px;
  background-color: white;
  font-size: 14px;
  color: var(--cs-d-text);
  cursor: pointer;
}

.filter-select:focus {
  outline: none;
  border-color: var(--cs-d-link);
}

.loading-state,
.error-state,
.no-reviews {
  padding: 40px;
  text-align: center;
  color: var(--cs-d-muted);
}

.error-state {
  color: var(--cs-d-error);
}

.reviews-container {
  margin-bottom: 24px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
}

.page-btn {
  padding: 8px 16px;
  background-color: #1a73e8;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.page-btn:hover:not(:disabled) {
  background-color: #1557b0;
}

.page-btn:disabled {
  background-color: #dadce0;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: var(--cs-d-muted);
}

@media (max-width: 768px) {
  .review-filters {
    flex-direction: column;
  }
  
  .filter-select {
    width: 100%;
  }
}
</style>

